<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { VueSpinner } from 'vue3-spinners'
import { useFormatStore } from '@/stores/formatStore'
import SpecialRequest from "@/components/SpecialRequest/SpecialRequest.vue";

const { t } = useI18n()
const formatStore = useFormatStore()

defineProps<{
  availabilities: any[]
  formatDate: (availability: any) => string
  canMovePrev: boolean
  canMoveNext: boolean
  isMobile: boolean
}>()

defineEmits<{
  (e: 'select', index: number): void
  (e: 'prev'): void
  (e: 'next'): void
}>()
</script>


<template>
  <div class="boat-data-header-bottom align-center pt-11 pb-2.5 px-10 flex flex-col lg:flex-row justify-between">

    <!-- Availability -->
    <div class="availability-content sm:mb-8 flex gap-2 lg:gap-6 items-center w-4/5">
      <div class="font-bold responsive-body-large">
        {{ t('availability') }}
      </div>

      <div class="availability-content__date flex-wrap flex md:flex-nowrap md:items-center gap-x-6">

        <!-- Previous -->
        <span
            :class="[
            'flex flex-col justify-center items-center',
            canMovePrev ? 'cursor-pointer' : 'text-gray-400 cursor-not-allowed'
          ]"
            @click="canMovePrev && $emit('prev')"
        >
          <img src="@/assets/icons/svg/Left_24x24.svg" alt="Previous" />
        </span>

        <!-- Dates -->
        <div
            v-for="(availability, idx) in availabilities"
            :key="idx"
            v-show="!isMobile || idx === 1"
            :class="{
            'date cursor-pointer flex flex-col items-center justify-center responsive-body-medium min-w-[12vw] min-h-20': true,
            'border-b-2 border-primary-blue': idx === 1,
            'border-b-2 border-custom-grey text-gray-400 cursor-not-allowed':
              idx !== 1 && (availability?.offerPrice ?? 0) <= 0,
            'border-b-2 border-custom-grey transition-[border-color]':
              idx !== 1 && (availability?.offerPrice ?? 0) > 0,
          }"
            @click="$emit('select', idx)"
        >
          <div class="flex flex-col">

            <div class="flex">
              {{ formatDate(availability) }}
            </div>

            <!-- Spinner -->
            <div class="flex justify-center gap-1" v-show="availability?.state === 'loading'">
              <VueSpinner size="25" class="p-2" color="#1b84ff" />
            </div>

            <!-- Price -->
            <div class="flex justify-center gap-1" v-show="availability?.state === 'idle'">
              <span v-if="(availability?.offerPrice ?? 0) > 0">
                {{ t('from') }}
                <span class="font-semibold">
                  {{ formatStore.formatCurrency(availability.offerPrice) }}
                </span>
              </span>
              <span v-else>{{ t('notAvailable') }}</span>
            </div>

          </div>
        </div>

        <!-- Next -->
        <span
            class="cursor-pointer flex flex-col justify-center items-center"
            @click="$emit('next')"
        >
          <img src="@/assets/icons/svg/Right_24x24.svg" alt="Next" />
        </span>

      </div>
    </div>

    <!-- Special Request -->
    <div class="boat-data-header-bottom-right flex gap-x-6 align-center justify-center items-center mb-8">
      <div class="flex align-center gap-x-2">
        <div class="send-request-text responsive-body-medium text-no-wrap md:text-wrap">
          {{ t('specialRequest') }}
        </div>
        <SpecialRequest :t="t" />
      </div>
    </div>

  </div>
</template>

<style scoped>


@media (max-width: 1450px) {
  .availability-content__date {
    column-gap: 1.25rem;
  }

  .boat-data-header-bottom-right {
    width: 25%;

  }
}

@media (max-width: 1280px) {
  .availability-content {
    flex-direction: column;
    justify-content: center;
    width: 100%;
    margin-bottom: 20px;
  }

  .availability-content__date {
    width: unset;
    column-gap: 1.5rem;
  }

  .boat-data-header-bottom-right {
    row-gap: 1.25rem;
    width: 45%;
  }
}

@media (max-width: 580px) {
  .boat-data-header-bottom {
    padding-left: 10px !important;
    padding-right: 20px !important;
  }

  .boat-data-header-bottom-right {
    row-gap: 1.25rem;
    width: 100%;
  }
}


</style>
